import { Building2, Plus, MapPin, X, Loader2, MoreVertical, Trash2, Edit3, ExternalLink, Lock, CheckCircle2, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAppContext } from '../context/useAppContext';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/formatters';
import EmptyState from '../components/EmptyState';
import '../components/EmptyState.css';

export default function PropertiesPage() {
    const { user } = useAppContext();
    const navigate = useNavigate();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [showUpgradeGate, setShowUpgradeGate] = useState(false);
    const [properties, setProperties] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Modal Form State
    const [editingProperty, setEditingProperty] = useState<any>(null);
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [rent, setRent] = useState('');
    const [dueDay, setDueDay] = useState('1');
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchProperties = async () => {
        if (!user.id) return;
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('properties')
                .select('*')
                .eq('owner_id', user.id)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Error fetching properties:', error);
            } else {
                setProperties(data || []);
            }
        } catch (err) {
            console.error('Unexpected error fetching properties:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProperties();
    }, [user.id]);

    const handleAddClick = () => {
        if (user.plan === 'Free' && properties.length >= 1) {
            setShowUpgradeGate(true);
            return;
        }
        setIsAddModalOpen(true);
    };

    const handleCreateOrUpdateProperty = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name || !rent || !user.id) return;

        // Guard: re-check limit at submit time (catches stale state / race conditions)
        if (!editingProperty && user.plan === 'Free' && properties.length >= 1) {
            setIsAddModalOpen(false);
            setShowUpgradeGate(true);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                owner_id: user.id,
                name,
                address,
                rent_amount: Number(rent),
                due_day: Number(dueDay),
                currency: 'EUR'
            };

            let error;
            if (editingProperty) {
                const { error: updateError } = await supabase
                    .from('properties')
                    .update(payload)
                    .eq('id', editingProperty.id);
                error = updateError;
            } else {
                const { error: insertError } = await supabase
                    .from('properties')
                    .insert(payload);
                error = insertError;
            }

            if (error) {
                // RLS blocked the insert — free plan limit hit; show upgrade modal
                if (error.code === '42501' || error.message?.includes('row-level security')) {
                    setIsAddModalOpen(false);
                    setEditingProperty(null);
                    setShowUpgradeGate(true);
                } else {
                    console.error('Error saving property:', error);
                    alert('Error: ' + error.message);
                }
            } else {
                setIsAddModalOpen(false);
                setEditingProperty(null);
                setName(''); setAddress(''); setRent(''); setDueDay('1');
                fetchProperties();
            }
        } catch (err) {
            console.error('Unexpected error saving property:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteProperty = async (id: string) => {
        if (!confirm('Are you sure you want to delete this property? All associated data will be removed.')) return;
        
        try {
            const { error } = await supabase
                .from('properties')
                .delete()
                .eq('id', id);

            if (error) {
                alert('Error deleting property: ' + error.message);
            } else {
                fetchProperties();
            }
        } catch (err) {
            console.error('Delete error:', err);
        }
    };

    const openEditModal = (prop: any) => {
        setEditingProperty(prop);
        setName(prop.name);
        setAddress(prop.address || '');
        setRent(prop.rent_amount.toString());
        setDueDay(prop.due_day.toString());
        setIsAddModalOpen(true);
        setActiveMenu(null);
    };

    return (
        <div className="page dashboard-page">
            <div className="page__header dashboard-page-header">
                <div className="dashboard-page-heading">
                    <h1 className="page__title" style={{ fontSize: '2.5rem', fontWeight: 900, letterSpacing: '-0.04em', marginBottom: '8px', color: '#1a1d2e' }}>Properties</h1>
                    <p className="page__subtitle" style={{ fontSize: '1.125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Manage and monitor your real estate portfolio</p>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={handleAddClick}
                    disabled={loading}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        borderRadius: '14px',
                        padding: '0.75rem 1.75rem',
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                        border: 'none',
                        boxShadow: '0 10px 15px -3px rgba(22,163,74,0.25)'
                    }}
                >
                    <Plus size={20} /> Add Property
                </button>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-16)' }}>
                    <Loader2 className="animate-spin" size={40} color="var(--color-primary)" />
                </div>
            ) : properties.length === 0 ? (
                <EmptyState
                    icon={<Building2 />}
                    title="No properties found"
                    description="You haven't added any properties yet. Start by adding your first rental unit to track tenants and rent."
                    actionLabel="Add Property"
                    onAction={handleAddClick}
                />
            ) : (
                <div className="property-grid" style={{ gap: 'var(--space-6)', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))' }}>
                    {properties.map((property) => {
                        return (
                            <div
                                key={property.id}
                                className="property-card"
                                onClick={() => navigate(`/app/landlord/properties/${property.id}`)}
                                style={{ 
                                    padding: '2rem', 
                                    borderRadius: '24px', 
                                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 10px 15px -3px rgba(0,0,0,0.03)',
                                    border: '1px solid #f1f5f9',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '1.25rem',
                                    background: '#fff',
                                    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                                    position: 'relative',
                                    zIndex: activeMenu === property.id ? 10 : 1
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 24px -10px rgba(0, 0, 0, 0.08)'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.02), 0 10px 15px -3px rgba(0,0,0,0.03)'; e.currentTarget.style.borderColor = '#f1f5f9'; }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: 'var(--color-text)', letterSpacing: '-0.01em' }}>{property.name}</h3>
                                    <div style={{ position: 'relative' }}>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === property.id ? null : property.id); }}
                                            style={{ background: activeMenu === property.id ? 'var(--color-bg-subtle)' : 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)', padding: '4px', borderRadius: '50%' }}
                                        >
                                            <MoreVertical size={20} />
                                        </button>
                                        
                                        {activeMenu === property.id && (
                                            <div className="dropdown-menu" style={{ 
                                                display: 'block',
                                                position: 'absolute', 
                                                top: '100%', 
                                                right: 0, 
                                                background: '#fff', 
                                                boxShadow: '0 20px 40px -8px rgba(0,0,0,0.15)', 
                                                borderRadius: '18px', 
                                                padding: '8px', 
                                                zIndex: 500, 
                                                width: '200px', 
                                                border: '1px solid #e2e8f0', 
                                                marginTop: '10px',
                                                textAlign: 'left',
                                                animation: 'slideUp 0.2s ease-out'
                                            }}>
                                                <div style={{ padding: '10px 14px', fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Management</div>
                                                <button onClick={(e) => { e.stopPropagation(); openEditModal(property); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'none', border: 'none', borderRadius: '12px', cursor: 'pointer', textAlign: 'left', fontSize: '0.9375rem', fontWeight: 600, color: 'var(--color-text)', transition: 'all 0.2s' }} className="menu-item-hover">
                                                    <div style={{ background: '#f8fafc', padding: '6px', borderRadius: '8px' }}><Edit3 size={14} /></div> Edit Details
                                                </button>
                                                <div style={{ height: '1px', background: '#f1f5f9', margin: '6px 0' }}></div>
                                                <button onClick={(e) => { e.stopPropagation(); handleDeleteProperty(property.id); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'rgba(239, 68, 68, 0.03)', border: 'none', borderRadius: '12px', cursor: 'pointer', textAlign: 'left', fontSize: '0.9375rem', fontWeight: 700, color: '#dc2626', transition: 'all 0.2s' }}>
                                                    <div style={{ background: 'rgba(239, 68, 68, 0.08)', padding: '6px', borderRadius: '8px' }}><Trash2 size={14} /></div> Delete Property
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-text-tertiary)', fontSize: '0.875rem', marginTop: '-4px' }}>
                                    <MapPin size={14} opacity={0.7} />
                                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {property.address || 'No address set'}
                                    </span>
                                </div>

                                <div className="dashboard-form-grid--equal" style={{ 
                                    display: 'grid', 
                                    gap: 'var(--space-4)', 
                                    padding: 'var(--space-5) 0',
                                    marginTop: 'var(--space-2)',
                                    borderTop: '1px solid var(--color-border-subtle)',
                                    borderBottom: '1px solid var(--color-border-subtle)'
                                }}>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Monthly Rent</div>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--color-primary)' }}>{formatCurrency(property.rent_amount)}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: property.status === 'occupied' ? 'var(--color-green)' : 'var(--color-text-tertiary)' }}></div>
                                            <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--color-text)' }}>{property.status === 'occupied' ? 'Occupied' : 'Vacant'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginTop: '0.5rem' }}>
                                    <button 
                                        className="btn btn-primary" 
                                        style={{ 
                                            width: '100%', 
                                            padding: '1rem', 
                                            borderRadius: '16px', 
                                            fontWeight: 700, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            gap: '10px', 
                                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)', 
                                            fontSize: '1rem', 
                                            border: 'none', 
                                            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer'
                                        }}
                                        onClick={(e) => { e.stopPropagation(); navigate(`/app/landlord/properties/${property.id}`); }}
                                    >
                                        View Details <ExternalLink size={18} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Plan Gating Modal */}
            {showUpgradeGate && (
                <div
                    className="dropdown-overlay dashboard-modal-shell"
                    onClick={() => setShowUpgradeGate(false)}
                    style={{ background: 'rgba(15, 23, 42, 0.65)', animation: 'fadeIn 0.2s ease-out', backdropFilter: 'blur(4px)' }}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: '28px',
                            padding: '0',
                            maxWidth: '460px',
                            width: '90%',
                            boxShadow: '0 30px 60px -12px rgba(0,0,0,0.35)',
                            animation: 'slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                            overflow: 'hidden',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header gradient banner */}
                        <div style={{
                            background: 'linear-gradient(135deg, #15803d 0%, #7c3aed 100%)',
                            padding: '2.5rem 2.5rem 3.5rem',
                            position: 'relative',
                            overflow: 'hidden',
                        }}>
                            <button
                                onClick={() => setShowUpgradeGate(false)}
                                style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', color: '#fff', padding: '6px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <X size={16} />
                            </button>
                            <div style={{ width: '52px', height: '52px', background: 'rgba(255,255,255,0.15)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                                <Lock size={24} color="#fff" />
                            </div>
                            <h3 style={{ margin: '0 0 8px', fontSize: '1.625rem', fontWeight: 900, color: '#fff', letterSpacing: '-0.03em' }}>
                                Unlock More Properties
                            </h3>
                            <p style={{ margin: 0, fontSize: '0.9375rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, lineHeight: 1.5 }}>
                                You're on the Free plan — limited to 1 property. Upgrade to add unlimited properties and grow your portfolio.
                            </p>
                            <Building2 size={120} style={{ position: 'absolute', bottom: '-20px', right: '-10px', opacity: 0.1, transform: 'rotate(-10deg)' }} />
                        </div>

                        {/* Features list */}
                        <div style={{ padding: '2rem 2.5rem', borderBottom: '1px solid #f1f5f9' }}>
                            <p style={{ margin: '0 0 1rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8' }}>What you get with Pro — €15/mo</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {[
                                    'Unlimited properties in your portfolio',
                                    'Document e-signing for lease agreements',
                                    'Priority support from our team',
                                ].map(feature => (
                                    <div key={feature} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9375rem', fontWeight: 600, color: '#1e293b' }}>
                                        <CheckCircle2 size={18} color="#16a34a" style={{ flexShrink: 0 }} />
                                        {feature}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Actions */}
                        <div style={{ padding: '1.75rem 2.5rem', display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => { setShowUpgradeGate(false); navigate('/app/landlord/dashboard'); }}
                                style={{ flex: 1, padding: '0.875rem', borderRadius: '14px', border: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 600, fontSize: '0.9375rem', color: '#64748b', cursor: 'pointer' }}
                            >
                                Maybe later
                            </button>
                            <button
                                onClick={() => { setShowUpgradeGate(false); navigate('/app/landlord/settings'); }}
                                style={{ flex: 2, padding: '0.875rem', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', fontWeight: 800, fontSize: '0.9375rem', color: '#fff', cursor: 'pointer', boxShadow: '0 8px 20px -6px rgba(22,163,74,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                            >
                                <Zap size={16} /> Upgrade to Pro
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Property Modal */}
            {isAddModalOpen && (
                <div 
                    className="dropdown-overlay dashboard-modal-shell" 
                    onClick={() => { setIsAddModalOpen(false); setEditingProperty(null); }} 
                    style={{ 
                        background: 'rgba(15, 23, 42, 0.65)'
                    }}
                >
                    <div
                        className="dashboard-modal"
                        style={{ 
                            background: '#fff',
                            padding: '3.5rem', 
                            boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.35)', 
                            animation: 'slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '3rem' }}>
                            <div>
                                <div style={{ display: 'inline-flex', padding: '10px', borderRadius: '14px', background: 'rgba(22,163,74,0.08)', color: '#16a34a', marginBottom: '1rem' }}>
                                    <Building2 size={24} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '2rem', fontWeight: 900, letterSpacing: '-0.04em', color: '#1a1d2e' }}>{editingProperty ? 'Edit Property' : 'Add Property'}</h3>
                                <p style={{ fontSize: '1rem', color: 'var(--color-text-secondary)', marginTop: '8px', fontWeight: 500 }}>{editingProperty ? 'Update property details and rent settings' : 'Create a new rental unit in your portfolio'}</p>
                            </div>
                            <button
                                onClick={() => { setIsAddModalOpen(false); setEditingProperty(null); }}
                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', color: '#64748b', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateOrUpdateProperty} style={{ gap: '2.5rem', display: 'flex', flexDirection: 'column' }}>
                            <div className="auth-field">
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Property Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Maple Street Apartment"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                    style={{ borderRadius: '16px', padding: '1.25rem', border: '1px solid #e2e8f0', fontSize: '1.0625rem', width: '100%', background: '#f8fafc', fontWeight: 600, color: '#1a1d2e', outline: 'none', transition: 'all 0.2s' }}
                                />
                            </div>
                            <div className="auth-field">
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Complete Address</label>
                                <input
                                    type="text"
                                    placeholder="e.g. 123 Maple St, Dublin, Ireland"
                                    value={address}
                                    onChange={e => setAddress(e.target.value)}
                                    style={{ borderRadius: '16px', padding: '1.25rem', border: '1px solid #e2e8f0', fontSize: '1.0625rem', width: '100%', background: '#f8fafc', fontWeight: 600, color: '#1a1d2e', outline: 'none', transition: 'all 0.2s' }}
                                />
                            </div>
                            <div className="dashboard-form-grid">
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Monthly Rent (€)</label>
                                    <input
                                        type="number"
                                        placeholder="1200"
                                        value={rent}
                                        onChange={e => setRent(e.target.value)}
                                        required
                                        style={{ borderRadius: '16px', padding: '1.25rem', border: '1px solid #e2e8f0', fontSize: '1.0625rem', width: '100%', background: '#f8fafc', fontWeight: 600, color: '#1a1d2e', outline: 'none', transition: 'all 0.2s' }}
                                    />
                                </div>
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rent Due Day</label>
                                    <select
                                        value={dueDay}
                                        onChange={e => setDueDay(e.target.value)}
                                        style={{ width: '100%', padding: '1.25rem', borderRadius: '16px', border: '1px solid #e2e8f0', fontSize: '1.0625rem', background: '#f8fafc', fontWeight: 600, cursor: 'pointer', appearance: 'none' }}
                                    >
                                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                            <option key={d} value={d}>Day {d}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="dashboard-modal__actions" style={{ paddingTop: '2.5rem' }}>
                                <button type="button" className="btn btn-secondary" onClick={() => { setIsAddModalOpen(false); setEditingProperty(null); }} style={{ padding: '1.125rem 2.25rem', borderRadius: '16px', fontWeight: 700, fontSize: '1rem' }}>Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting || !name || !rent} style={{ padding: '1.125rem 3.5rem', borderRadius: '16px', fontWeight: 800, fontSize: '1rem', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', boxShadow: '0 12px 24px -6px rgba(79, 70, 229, 0.4)' }}>
                                    {isSubmitting ? (editingProperty ? 'Saving...' : 'Creating...') : (editingProperty ? 'Save Changes' : 'Add Property')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                .menu-item-hover:hover { background: #f8fafc !important; }
            `}</style>
        </div>
    );
}
